import ApiService from "@/core/services/ApiService";

/**
 * @description Cliente HTTP del modulo de administracion de notificaciones
 * (compania). Sigue la misma convencion que el resto de services (p.ej.
 * SkillsSimulationService): wrapper delgado sobre ApiService (axios +
 * Bearer JWT via ApiService.setHeader/JwtService), sin logica de estado —
 * el estado vive en AdminNotificationsModule (Vuex).
 *
 * IMPORTANTE: este backend TODAVIA NO EXISTE en SeccuriBackend. Este
 * archivo documenta el contrato PROPUESTO para que Andres lo implemente
 * alla; mientras tanto, cualquier llamada real fallara (network error o
 * 404) y AdminNotificationsModule la maneja de forma segura (mismo patron
 * de catch/loading que SkillsSimulationModule).
 *
 * Contrato propuesto (envelope Result<T> — Seccuri.Shared.Results.Result<T>
 * — mismo que el resto de la app: `{ data, errors: [{ error, code }],
 * succeeded, message }`):
 *
 *  POST /admin-notifications
 *    Body: { title?: string, message: string, type: "success"|"error"|"warning"|"info",
 *            action?: { label: string, routeName: string } }
 *    `action` es opcional: si el admin elige un destino (ver
 *    ACTION_LINK_OPTIONS en AdminNotifications.vue), se guarda el nombre
 *    de ruta interno del frontend (routeName, p.ej. "edit-profile" o
 *    "SkillsSimulationSkillPicker") tal cual — el backend NO necesita
 *    entender que es una ruta, solo guardarlo y devolverlo igual; el
 *    frontend es quien lo resuelve con vue-router.
 *    Auth: [Authorize] — el backend DEBE validar que el usuario autenticado
 *    sea la cuenta contact@seccuri.com (o el rol/claim equivalente que se
 *    defina alla). La restriccion de UI en el frontend (router meta
 *    superAdminOnly + filtro de menu) es solo de navegacion — no
 *    reemplaza la validacion server-side.
 *    Efecto: crea el registro y lo deja disponible para TODOS los
 *    usuarios Talent (no hay seleccion de destinatarios en esta fase).
 *    Response: Result<AdminNotificationRecord> con el registro creado
 *    (incluyendo id y createdAt asignados por el backend).
 *
 *  GET /admin-notifications/history?page=&pageSize=
 *    Historial de notificaciones creadas por el admin, mas reciente
 *    primero. Response: Result<{ items: AdminNotificationRecord[], total: number }>.
 *
 * El lado TALENT (consumo — que estas notificaciones lleguen a la
 * campanita real de cada Talent) NO usa este service: vive en
 * NotificationsInboxService.ts + useAdminNotificationInbox.ts, con su
 * propio endpoint GET /notifications?since= (mismos datos, autorizacion
 * distinta — cualquier Talent, no solo contact@seccuri.com). Ver ese
 * archivo para el contrato completo.
 */

export type AdminNotificationType = "success" | "error" | "warning" | "info";

// Boton de accion opcional dentro de la notificacion (ej. "Completar
// perfil" -> ruta edit-profile). routeName es el `name` de una ruta de
// vue-router (ver ACTION_LINK_OPTIONS en AdminNotifications.vue) — el
// backend lo persiste tal cual, sin interpretarlo.
export interface AdminNotificationAction {
  label: string;
  routeName: string;
}

export interface AdminNotificationRecord {
  id: string;
  title?: string;
  message: string;
  type: AdminNotificationType;
  action?: AdminNotificationAction;
  createdAt: string; // ISO 8601, asignado por el backend
  createdBy?: string; // email del admin que la creo (contact@seccuri.com)
  recipientCount?: number; // opcional: cuantos Talent la recibieron
}

export interface CreateAdminNotificationPayload {
  title?: string;
  message: string;
  type: AdminNotificationType;
  action?: AdminNotificationAction;
}

export interface AdminNotificationsHistoryResponse {
  items: Array<AdminNotificationRecord>;
  total: number;
}

const BASE_PATH = "admin-notifications";

export default class AdminNotificationsService {
  static create(payload: CreateAdminNotificationPayload) {
    return ApiService.post(BASE_PATH, payload);
  }

  static getHistory(page = 1, pageSize = 20) {
    return ApiService.query(`${BASE_PATH}/history`, {
      params: { page, pageSize },
    });
  }
}
